import { useMemo } from "react";
import { Box, Grid } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import {
  WBButton,
  WBFlex,
  WBIcon,
  WBIconButton,
  WBNoResults,
  WBS3Card,
} from "@apptractive/design-system-web";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Product } from "../../API";
import { Link } from "../../components/Link";
import { PageContainer } from "../../components/PageContainer";
import { CSGetSub as GET_SUB } from "../../customGraphql/queries";
import {
  getUser as GET_USER,
  listProducts as LIST_PRODUCTS,
  productsByUser as PRODUCTS_BY_USER,
} from "../../graphql/queries";
import { getExpiry } from "../../helpers/datetime";
import { PATHS } from "../../navigation/paths";

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const { data: subData } = useQuery(gql(GET_SUB));
  const { data: userData } = useQuery(gql(GET_USER), {
    variables: {
      id: subData?.sub,
    },
    skip: !subData?.sub,
  });

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  const {
    data: productsData,
    refetch: refetchProducts,
    loading: productsLoading,
  } = useQuery(gql(LIST_PRODUCTS), {
    skip: !!userId,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: userProductsData,
    refetch: refetchUserProducts,
    loading: userProductListLoading,
  } = useQuery(gql(PRODUCTS_BY_USER), {
    skip: !userId,
    variables: {
      owner: `${userId}::${userId}`,
      sortDirection: "DESC",
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const products = useMemo(() => {
    if (productsData?.listProducts?.items) {
      return productsData?.listProducts?.items?.filter(
        (product: Product) => product.owner !== subData?.sub
      );
    } else if (userProductsData?.productsByUser?.items) {
      return userProductsData?.productsByUser?.items;
    }

    return [];
  }, [productsData, userProductsData, subData]);

  return (
    <PageContainer>
      <WBFlex p={2}>
        <Box flex={1}>
          <WBIconButton
            icon="Refresh"
            size="small"
            onClick={() => (userId ? refetchUserProducts() : refetchProducts())}
          />
        </Box>
        <Box flex={1} textAlign="right">
          {userId && (
            <WBButton
              startIcon={<WBIcon name="Add" size="small" />}
              onClick={() =>
                navigate(`${PATHS.productCreate}?category=${user.country}`)
              }
              size="small"
              type="button"
            >
              {t("productTitle", { ns: "products" })}
            </WBButton>
          )}
        </Box>
      </WBFlex>
      <Grid container spacing={2}>
        {products.map((product: Product) => (
          <Grid item xs={12} sm={12} md={6} lg={4} key={product.id}>
            <WBS3Card
              image={product.images?.[0]}
              title={product.title}
              subTitle={product.expiresAt ? getExpiry(product.expiresAt) : ""}
              description={product.description}
              cardActions={
                <>
                  <Link to={`${PATHS.productView}/${product.id}`}>
                    {t("viewTitle", { ns: "common" })}
                  </Link>

                  <Link
                    to={`${PATHS.productUpdate}/${product.id}?category=${product.category}`}
                  >
                    {t("updateTitle", { ns: "common" })}
                  </Link>
                </>
              }
            />
          </Grid>
        ))}
      </Grid>

      {!productsLoading &&
        !userProductListLoading &&
        products?.length === 0 &&
        subData?.sub === user?.id && (
          <WBFlex flexDirection="column" justifyContent="center" flex={1}>
            <WBNoResults
              title={t("noUserProductsTitle", { ns: "products" })}
              description={t("noUserProductsDescription", { ns: "products" })}
              btnTitle={t("getStartedTitle", { ns: "common" })}
              onClick={() =>
                navigate(`${PATHS.productCreate}?category=${user.country}`)
              }
            />
          </WBFlex>
        )}
    </PageContainer>
  );
};
export default Products;
